import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { InputNumber, Switcher } from '../../uiComponents/common';
import { FormProvider } from 'react-hook-form';
import { Show, Table, TableCell } from '../../components';
import { useTranslation } from 'react-i18next';
import './productsSoldout.css';

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const AREA_FIELDS = [
  'price',
  'priceExtraPax',
  'consumption',
  'consumptionPerExtraPax',
  'tipPercentage',
  'taxPercentage',
  'depositPercentage',
  'stock',
];
const GROUP_FIELDS = ['price', 'tipPercentage', 'taxPercentage', 'depositPercentage'];

const CopyButton = ({ weekDay, methods, fields, t }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const copyTo = useCallback(
    (targetDays) => {
      const values = methods.getValues();
      targetDays.forEach((day) => {
        if (day === weekDay) return;
        fields.forEach((field) => {
          methods.setValue(`${field}_${day}`, values[`${field}_${weekDay}`]);
        });
        methods.setValue(`isActive_${day}`, values[`isActive_${weekDay}`]);
      });
      setOpen(false);
    },
    [weekDay, methods, fields]
  );

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right });
    }
    setOpen(!open);
  };

  return (
    <div ref={btnRef}>
      <button
        type='button'
        title={t('sectionPrices.copyToAll')}
        style={{
          background: 'none',
          border: '1px solid var(--color-border-table)',
          borderRadius: '6px',
          cursor: 'pointer',
          padding: '4px 6px',
          display: 'flex',
          alignItems: 'center',
          color: 'var(--color-secondary)',
          transition: 'all 0.15s',
        }}
        onClick={handleToggle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--color-background-sidebar)';
          e.currentTarget.style.color = 'var(--color-primary, #6941C6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'none';
          e.currentTarget.style.color = 'var(--color-secondary)';
        }}
      >
        <i className='far fa-clone' style={{ fontSize: '14px' }} />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              transform: 'translateX(-100%)',
              background: '#fff',
              border: '1px solid var(--color-border-table)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 9999,
              minWidth: '190px',
              overflow: 'hidden',
            }}
          >
            <button
              type='button'
              className='copy-dropdown-item'
              onClick={() => copyTo(DAYS)}
            >
              {t('sectionPrices.copyToAll')}
            </button>
            <button
              type='button'
              className='copy-dropdown-item'
              onClick={() => {
                const values = methods.getValues();
                const activeDays = DAYS.filter((d) => values[`isActive_${d}`]);
                copyTo(activeDays);
              }}
            >
              {t('sectionPrices.copyToActive')}
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

const ProductsSoldout = ({ methods, data, mode }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'forms.products' });
  const fields = mode === 'areas' ? AREA_FIELDS : GROUP_FIELDS;

  const productsRow =
    mode === 'areas'
      ? [
          { title: t('sectionPrices.table.day') },
          { title: '' },
          { title: t('sectionPrices.table.price') },
          { title: t('sectionPrices.table.pricePax') },
          {
            title: t('sectionPrices.table.consumption'),
            tooltip: t('sectionPrices.tooltip.consumption'),
          },
          {
            title: t('sectionPrices.table.consumptionPerExtraPax'),
          },
          { title: t('sectionPrices.table.tip'), tooltip: t('sectionPrices.tooltip.tip') },
          { title: t('sectionPrices.table.tax'), tooltip: t('sectionPrices.tooltip.tax') },
          {
            title: t('sectionPrices.table.deposit'),
            tooltip: t('sectionPrices.tooltip.deposit'),
          },
          { title: t('sectionPrices.table.stock'), tooltip: t('sectionPrices.tooltip.stock') },
          { title: t('sectionPrices.table.copy') },
        ]
      : [
          { title: t('sectionPrices.table.day') },
          { title: '' },
          { title: t('sectionPrices.table.price') },
          { title: t('sectionPrices.table.tip'), tooltip: t('sectionPrices.tooltip.tip') },
          { title: t('sectionPrices.table.tax'), tooltip: t('sectionPrices.tooltip.tax') },
          {
            title: t('sectionPrices.table.deposit'),
            tooltip: t('sectionPrices.tooltip.deposit'),
          },
          { title: t('sectionPrices.table.copy') },
        ];

  return (
    <>
      <FormProvider {...methods}>
        <Show.When isTrue={mode === 'groups'}>
          <Table
            data={data ? data : []}
            showPagination={false}
            borderTop={false}
            borderContainer={true}
            columns={productsRow}
            renderRow={(products, index) => (
              <>
                <TableCell style={{ verticalAlign: 'baseline' }} className='pr-1'>
                  {t(`sectionPrices.weekday.${products.weekDay}`)}
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                  <Switcher
                    name={'isActive_' + products.weekDay}
                    onClick={() => {
                      methods.clearErrors('price_' + products.weekDay);
                      methods.clearErrors('tipPercentage_' + products.weekDay);
                      methods.clearErrors('taxPercentage_' + products.weekDay);
                      methods.clearErrors('depositPercentage_' + products.weekDay);
                    }}
                  />
                </TableCell>

                <TableCell style={{ verticalAlign: 'baseline', minWidth: '130px' }}>
                  <InputNumber
                    name={'price_' + products.weekDay}
                    {...methods.register(`price_${products.weekDay}`, {
                      required: methods.watch(`isActive_${products.weekDay}`)
                        ? t('required')
                        : false,
                    })}
                    iconPadding={'pl-2'}
                    className='hideArrow'
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    icon={<span style={{ fontSize: '16px' }}>$</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '100px' }}>
                  <InputNumber
                    name={'tipPercentage_' + products.weekDay}
                    {...methods.register(`tipPercentage_${products.weekDay}`, {
                      required: methods.watch(`isActive_${products.weekDay}`)
                        ? t('required')
                        : false,
                    })}
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    iconPadding={'pl-2'}
                    maxNumber={100}
                    minNumber={0}
                    className='hideArrow'
                    icon={<span style={{ fontSize: '16px' }}>%</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '100px' }}>
                  <InputNumber
                    name={'taxPercentage_' + products.weekDay}
                    {...methods.register(`taxPercentage_${products.weekDay}`, {
                      required: methods.watch(`isActive_${products.weekDay}`)
                        ? t('required')
                        : false,
                    })}
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    iconPadding={'pl-2'}
                    maxNumber={100}
                    minNumber={0}
                    className='hideArrow'
                    icon={<span style={{ fontSize: '16px' }}>%</span>}
                  />
                </TableCell>
                <TableCell style={{ minWidth: '100px', verticalAlign: 'baseline' }}>
                  <InputNumber
                    name={'depositPercentage_' + products.weekDay}
                    {...methods.register(`depositPercentage_${products.weekDay}`, {
                      required: methods.watch(`isActive_${products.weekDay}`)
                        ? t('required')
                        : false,
                    })}
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    iconPadding={'pl-2'}
                    maxNumber={100}
                    minNumber={0}
                    className='hideArrow'
                    icon={<span style={{ fontSize: '16px' }}>%</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                  <CopyButton
                    weekDay={products.weekDay}
                    methods={methods}
                    fields={fields}
                    t={t}
                  />
                </TableCell>
              </>
            )}
          />
        </Show.When>
        <Show.When isTrue={mode === 'areas'}>
          <Table
            data={data ?? []}
            showPagination={false}
            borderTop={false}
            borderContainer={true}
            columns={productsRow}
            renderRow={(products, index) => (
              <>
                <TableCell style={{ verticalAlign: 'baseline' }} className='pr-1'>
                  {t(`sectionPrices.weekday.${products.weekDay}`)}
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                  <Switcher name={'isActive_' + products.weekDay} />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '130px' }}>
                  <InputNumber
                    name={'price_' + products.weekDay}
                    isRequired={true}
                    iconPadding={'pl-2'}
                    className='hideArrow'
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    icon={<span style={{ fontSize: '16px' }}>$</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '130px' }}>
                  <InputNumber
                    name={'priceExtraPax_' + products.weekDay}
                    iconPadding={'pl-2'}
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    className='hideArrow'
                    icon={<span style={{ fontSize: '16px' }}>$</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '130px' }}>
                  <InputNumber
                    name={'consumption_' + products.weekDay}
                    iconPadding={'pl-2'}
                    className='hideArrow'
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    isRequired={true}
                    icon={<span style={{ fontSize: '16px' }}>$</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '130px' }}>
                  <InputNumber
                    name={'consumptionPerExtraPax_' + products.weekDay}
                    iconPadding={'pl-2'}
                    className='hideArrow'
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    isRequired={true}
                    icon={<span style={{ fontSize: '16px' }}>$</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '100px' }}>
                  <InputNumber
                    name={'tipPercentage_' + products.weekDay}
                    isRequired={true}
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    iconPadding={'pl-2'}
                    maxNumber={100}
                    minNumber={0}
                    className='hideArrow'
                    icon={<span style={{ fontSize: '16px' }}>%</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '100px' }}>
                  <InputNumber
                    name={'taxPercentage_' + products.weekDay}
                    isRequired={true}
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    iconPadding={'pl-2'}
                    maxNumber={100}
                    minNumber={0}
                    className='hideArrow'
                    icon={<span style={{ fontSize: '16px' }}>%</span>}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline', minWidth: '100px' }}>
                  <InputNumber
                    name={'depositPercentage_' + products.weekDay}
                    isRequired={true}
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    iconPadding={'pl-2'}
                    maxNumber={100}
                    minNumber={0}
                    className='hideArrow'
                    icon={<span style={{ fontSize: '16px' }}>%</span>}
                  />
                </TableCell>
                <TableCell style={{ minWidth: '100px', verticalAlign: 'baseline' }}>
                  <InputNumber
                    className='hideArrow'
                    name={'stock_' + products.weekDay}
                    isDisabled={!methods.watch('isActive_' + products.weekDay)}
                    isRequired={true}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                  <CopyButton
                    weekDay={products.weekDay}
                    methods={methods}
                    fields={fields}
                    t={t}
                  />
                </TableCell>
              </>
            )}
          />
        </Show.When>
      </FormProvider>
    </>
  );
};

export default ProductsSoldout;
